/**
 * Module 33 — Motif / Texture registry (WP-BG.T).
 *
 * A general-purpose, extensible library of PROCEDURAL background textures for
 * every game, the lobby, and seasonal/occasion themes. Each motif is a small
 * pure function returning one or more CSS background layers, built from:
 *   - tiled inline SVG shapes,
 *   - CSS gradient patterns (repeating-linear / radial-gradient),
 *   - (later batches) SVG-filter materials (feTurbulence).
 * All procedural — no image files. Motifs are drawn as a subtle-to-bold white
 * watermark over the gradient; strength is controlled by `textureOpacity`.
 *
 * The four originals (bubbles/droplets/waves/tube-silhouettes) match the app's
 * shipping watersort backgrounds; the rest are new (from the reference set in
 * Issues/Backgrouds Reference). No intensity tagging — pick per background and
 * tune with the opacity slider (2026-07-19 decision).
 */
export const MOTIF_TILE_SIZE = 120;
// ── low-level helpers ──────────────────────────────────────────────────────
const svg = (body, w = MOTIF_TILE_SIZE, h = MOTIF_TILE_SIZE) => `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">${body}</svg>`;
const uri = (s) => `data:image/svg+xml,${encodeURIComponent(s)}`;
/** A tiled SVG layer (repeats at `size`px). */
const tile = (body, size = MOTIF_TILE_SIZE) => ({
    image: `url("${uri(svg(body, size, size))}")`,
    size: `${size}px ${size}px`,
    repeat: 'repeat',
});
/** A single non-repeating SVG layer scaled to cover the element. */
const cover = (body, w, h) => ({
    image: `url("${uri(svg(body, w, h))}")`,
    size: 'cover',
    repeat: 'no-repeat',
});
const grad = (image, size = 'auto', repeat = 'no-repeat') => ({
    image,
    size,
    repeat,
});
const o3 = (n) => Number(n.toFixed(3)).toString();
const white = (op) => `rgba(255, 255, 255, ${o3(op)})`;
// ── procedural SVG generators (deterministic — no randomness) ───────────────
function radialHalftoneBody(op) {
    const cx = 300;
    const cy = 300;
    let dots = '';
    for (let ring = 1; ring <= 14; ring++) {
        const rad = ring * 20;
        const count = Math.max(6, ring * 6);
        const dotR = 1.2 + ring * 0.5;
        const ringOp = Math.min(op, op * (0.4 + ring * 0.05));
        for (let i = 0; i < count; i++) {
            const a = (i / count) * Math.PI * 2;
            const x = (cx + Math.cos(a) * rad).toFixed(1);
            const y = (cy + Math.sin(a) * rad).toFixed(1);
            dots += `<circle cx="${x}" cy="${y}" r="${dotR.toFixed(1)}" fill="#fff" fill-opacity="${o3(ringOp)}"/>`;
        }
    }
    return dots;
}
function halftoneEdgeBody(op) {
    const w = 600;
    const h = 360;
    const cx = 300;
    const cy = 180;
    const maxD = Math.hypot(cx, cy);
    let dots = '';
    for (let y = 12; y < h; y += 18) {
        for (let x = 12; x < w; x += 18) {
            const d = Math.hypot(x - cx, y - cy) / maxD;
            const r = 0.4 + d * 3.4;
            if (r < 0.7)
                continue;
            dots += `<circle cx="${x}" cy="${y}" r="${r.toFixed(1)}" fill="#fff" fill-opacity="${o3(op)}"/>`;
        }
    }
    return dots;
}
/** `count` radial spokes from r1→r2 around (cx,cy) — used by gears and sun. */
function radialLines(cx, cy, r1, r2, count, op, width) {
    let out = '';
    for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2;
        const x1 = (cx + Math.cos(a) * r1).toFixed(1);
        const y1 = (cy + Math.sin(a) * r1).toFixed(1);
        const x2 = (cx + Math.cos(a) * r2).toFixed(1);
        const y2 = (cy + Math.sin(a) * r2).toFixed(1);
        out += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="${width}" stroke-linecap="round"/>`;
    }
    return out;
}
function gearBody(op) {
    return (radialLines(60, 60, 22, 32, 8, op, 6) +
        `<circle cx="60" cy="60" r="22" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="4"/>` +
        `<circle cx="60" cy="60" r="8" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="4"/>`);
}
function sunBody(op) {
    return (radialLines(60, 60, 26, 40, 12, op, 3) +
        `<circle cx="60" cy="60" r="18" fill="#fff" fill-opacity="${o3(op)}"/>`);
}
/**
 * A full-cover procedural material via SVG feTurbulence — the noise's luminance
 * drives a white watermark alpha (max = op). Batch 4 painterly textures.
 */
function turbulenceCover(id, type, baseFrequency, numOctaves, op) {
    const matrix = `0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  ${o3(op)} 0 0 0 0`;
    const body = `<defs><filter id="${id}" x="0" y="0" width="100%" height="100%">` +
        `<feTurbulence type="${type}" baseFrequency="${baseFrequency}" numOctaves="${numOctaves}" stitchTiles="stitch" result="n"/>` +
        `<feColorMatrix in="n" type="matrix" values="${matrix}"/>` +
        `</filter></defs><rect width="100%" height="100%" filter="url(#${id})"/>`;
    return cover(body, 400, 300);
}
function bokehBody(op) {
    const filter = '<defs><filter id="bk" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="7"/></filter></defs>';
    const orbs = [
        [45, 55, 24],
        [155, 85, 32],
        [95, 165, 18],
        [185, 185, 14],
        [30, 175, 12],
    ]
        .map(([x, y, r]) => `<circle cx="${x}" cy="${y}" r="${r}" fill="#fff" fill-opacity="${o3(op)}" filter="url(#bk)"/>`)
        .join('');
    return filter + orbs;
}
// ── the registry ────────────────────────────────────────────────────────────
export const MOTIF_LIST = [
    // — shapes (match shipping watersort backgrounds) —
    {
        id: 'bubbles',
        label: 'Bubbles',
        category: 'shapes',
        render: (op) => [
            tile(`<circle cx="20" cy="30" r="10" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<circle cx="80" cy="70" r="14" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<circle cx="55" cy="100" r="7" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'droplets',
        label: 'Droplets',
        category: 'shapes',
        render: (op) => [
            tile(`<path d="M30 10 C40 30 45 42 30 50 C15 42 20 30 30 10 Z" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<path d="M85 60 C95 80 100 92 85 100 C70 92 75 80 85 60 Z" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'waves',
        label: 'Waves',
        category: 'shapes',
        render: (op) => [
            tile(`<path d="M0 40 Q 30 20 60 40 T 120 40" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3" fill="none"/>` +
                `<path d="M0 90 Q 30 70 60 90 T 120 90" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3" fill="none"/>`),
        ],
    },
    {
        id: 'tube-silhouettes',
        label: 'Tube silhouettes',
        category: 'shapes',
        render: (op) => [
            tile(`<rect x="20" y="20" width="18" height="50" rx="9" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<rect x="70" y="45" width="18" height="50" rx="9" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    // — dots / halftone —
    {
        id: 'dot-grid',
        label: 'Dot grid',
        category: 'dots',
        render: (op) => [
            grad(`radial-gradient(circle at 50% 50%, ${white(op)} 1.6px, transparent 2.2px)`, '18px 18px', 'repeat'),
        ],
    },
    {
        id: 'halftone-radial',
        label: 'Halftone (radial rings)',
        category: 'dots',
        render: (op) => [cover(radialHalftoneBody(op), 600, 600)],
    },
    {
        id: 'halftone-edge',
        label: 'Halftone (edge-dense)',
        category: 'dots',
        render: (op) => [cover(halftoneEdgeBody(op), 600, 360)],
    },
    {
        id: 'bokeh',
        label: 'Bokeh orbs',
        category: 'dots',
        render: (op) => [tile(bokehBody(op), 220)],
    },
    // — lines —
    {
        id: 'diagonal-lines',
        label: 'Diagonal speed lines',
        category: 'lines',
        render: (op) => [
            grad(`repeating-linear-gradient(45deg, ${white(op)} 0 2px, transparent 2px 16px)`),
        ],
    },
    {
        id: 'action-lines',
        label: 'Action lines (horizontal)',
        category: 'lines',
        render: (op) => [
            grad(`repeating-linear-gradient(0deg, ${white(op)} 0 2px, transparent 2px 14px)`),
        ],
    },
    {
        id: 'vertical-lines',
        label: 'Vertical lines',
        category: 'lines',
        render: (op) => [
            grad(`repeating-linear-gradient(90deg, ${white(op)} 0 2px, transparent 2px 14px)`),
        ],
    },
    {
        id: 'grid',
        label: 'Grid',
        category: 'lines',
        render: (op) => [
            grad(`repeating-linear-gradient(0deg, ${white(op)} 0 1.5px, transparent 1.5px 24px)`),
            grad(`repeating-linear-gradient(90deg, ${white(op)} 0 1.5px, transparent 1.5px 24px)`),
        ],
    },
    {
        id: 'chevrons',
        label: 'Chevrons',
        category: 'lines',
        render: (op) => [
            tile(`<path d="M0 40 L30 15 L60 40" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>` +
                `<path d="M0 85 L30 60 L60 85" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`, 60),
        ],
    },
    // — geometric —
    {
        id: 'triangles',
        label: 'Triangles',
        category: 'geometric',
        render: (op) => [
            tile(`<path d="M60 22 L96 92 L24 92 Z" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`),
        ],
    },
    {
        id: 'rings',
        label: 'Concentric rings',
        category: 'geometric',
        render: (op) => [
            tile(`<circle cx="60" cy="60" r="36" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="2.5"/>` +
                `<circle cx="60" cy="60" r="23" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="2.5"/>` +
                `<circle cx="60" cy="60" r="10" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="2.5"/>`),
        ],
    },
    {
        id: 'hexagons',
        label: 'Hexagons',
        category: 'geometric',
        render: (op) => [
            tile(`<path d="M60 20 L94 40 L94 80 L60 100 L26 80 L26 40 Z" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`),
        ],
    },
    {
        id: 'diamonds',
        label: 'Diamonds',
        category: 'geometric',
        render: (op) => [
            tile(`<path d="M60 18 L98 60 L60 102 L22 60 Z" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`),
        ],
    },
    {
        id: 'crosses',
        label: 'Plus / crosses',
        category: 'geometric',
        render: (op) => [
            tile(`<path d="M52 34 h16 v18 h18 v16 h-18 v18 h-16 v-18 h-18 v-16 h18 z" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'mosaic',
        label: 'Mosaic squares',
        category: 'geometric',
        render: (op) => [
            tile(`<rect x="16" y="18" width="16" height="16" rx="3" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<rect x="74" y="30" width="24" height="24" rx="4" fill="#fff" fill-opacity="${o3(op * 0.8)}"/>` +
                `<rect x="40" y="70" width="12" height="12" rx="3" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<rect x="86" y="88" width="18" height="18" rx="4" fill="#fff" fill-opacity="${o3(op * 0.7)}"/>`),
        ],
    },
    // — occasion / seasonal —
    {
        id: 'stars',
        label: 'Stars',
        category: 'occasion',
        render: (op) => [
            tile(`<path d="M60 22 L70 50 L99 50 L75 68 L84 96 L60 78 L36 96 L45 68 L21 50 L50 50 Z" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'sparkles',
        label: 'Sparkles',
        category: 'occasion',
        render: (op) => [
            tile(`<path d="M60 28 C63 52 68 57 92 60 C68 63 63 68 60 92 C57 68 52 63 28 60 C52 57 57 52 60 28 Z" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<path d="M100 18 C101 27 103 29 112 30 C103 31 101 33 100 42 C99 33 97 31 88 30 C97 29 99 27 100 18 Z" fill="#fff" fill-opacity="${o3(op * 0.8)}"/>`),
        ],
    },
    {
        id: 'hearts',
        label: 'Hearts',
        category: 'occasion',
        render: (op) => [
            tile(`<path d="M60 90 C18 62 28 26 60 46 C92 26 102 62 60 90 Z" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'snowflakes',
        label: 'Snowflakes',
        category: 'occasion',
        render: (op) => [
            tile(`<g stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="2.5" stroke-linecap="round">` +
                `<line x1="60" y1="18" x2="60" y2="102"/><line x1="26" y1="39" x2="94" y2="81"/><line x1="94" y1="39" x2="26" y2="81"/>` +
                `<line x1="60" y1="30" x2="51" y2="39"/><line x1="60" y1="30" x2="69" y2="39"/>` +
                `<line x1="60" y1="90" x2="51" y2="81"/><line x1="60" y1="90" x2="69" y2="81"/></g>`),
        ],
    },
    {
        id: 'confetti',
        label: 'Confetti',
        category: 'occasion',
        render: (op) => [
            tile(`<rect x="20" y="24" width="10" height="15" rx="2" transform="rotate(22 25 31)" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<rect x="78" y="30" width="10" height="15" rx="2" transform="rotate(-30 83 37)" fill="#fff" fill-opacity="${o3(op * 0.8)}"/>` +
                `<rect x="46" y="66" width="10" height="15" rx="2" transform="rotate(48 51 73)" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<rect x="88" y="84" width="10" height="15" rx="2" transform="rotate(-15 93 91)" fill="#fff" fill-opacity="${o3(op * 0.7)}"/>`),
        ],
    },
    {
        id: 'leaves',
        label: 'Leaves',
        category: 'occasion',
        render: (op) => [
            tile(`<path d="M35 92 C35 52 58 30 92 30 C90 66 66 92 35 92 Z" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<path d="M42 85 L82 40" stroke="#fff" stroke-opacity="${o3(op * 0.5)}" stroke-width="2" fill="none"/>`),
        ],
    },
    {
        id: 'flowers',
        label: 'Flowers',
        category: 'occasion',
        render: (op) => [
            tile(`<g fill="#fff" fill-opacity="${o3(op)}"><circle cx="60" cy="34" r="12"/><circle cx="84" cy="52" r="12"/>` +
                `<circle cx="74" cy="80" r="12"/><circle cx="46" cy="80" r="12"/><circle cx="36" cy="52" r="12"/></g>` +
                `<circle cx="60" cy="58" r="10" fill="#fff" fill-opacity="${o3(op * 0.55)}"/>`),
        ],
    },
    {
        id: 'candy-canes',
        label: 'Candy canes',
        category: 'occasion',
        render: (op) => [
            tile(`<path d="M45 96 L45 48 A16 16 0 0 1 77 48" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="9" stroke-linecap="round"/>`),
        ],
    },
    // — themed icons (science / math / logic / game / joy) —
    {
        id: 'atoms',
        label: 'Atoms (science)',
        category: 'themed',
        render: (op) => [
            tile(`<g fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="2.5">` +
                `<ellipse cx="60" cy="60" rx="40" ry="15"/><ellipse cx="60" cy="60" rx="40" ry="15" transform="rotate(60 60 60)"/>` +
                `<ellipse cx="60" cy="60" rx="40" ry="15" transform="rotate(120 60 60)"/></g>` +
                `<circle cx="60" cy="60" r="5" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'flasks',
        label: 'Flasks (science)',
        category: 'themed',
        render: (op) => [
            tile(`<path d="M50 24 L50 52 L30 88 Q27 96 37 96 L83 96 Q93 96 90 88 L70 52 L70 24" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>` +
                `<line x1="46" y1="24" x2="74" y2="24" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`),
        ],
    },
    {
        id: 'math-symbols',
        label: 'Math symbols',
        category: 'themed',
        render: (op) => [
            tile(`<text x="16" y="52" font-size="34" font-family="sans-serif" fill="#fff" fill-opacity="${o3(op)}">+ −</text>` +
                `<text x="16" y="100" font-size="34" font-family="sans-serif" fill="#fff" fill-opacity="${o3(op)}">× ÷</text>`),
        ],
    },
    {
        id: 'pi-infinity',
        label: 'π / ∞ (math)',
        category: 'themed',
        render: (op) => [
            tile(`<text x="18" y="58" font-size="42" font-family="serif" fill="#fff" fill-opacity="${o3(op)}">π</text>` +
                `<text x="60" y="104" font-size="42" font-family="serif" fill="#fff" fill-opacity="${o3(op)}">∞</text>`),
        ],
    },
    {
        id: 'gears',
        label: 'Gears (logic)',
        category: 'themed',
        render: (op) => [tile(gearBody(op))],
    },
    {
        id: 'jigsaw',
        label: 'Jigsaw (puzzle)',
        category: 'themed',
        render: (op) => [
            tile(`<path d="M34 34 H52 A8 8 0 0 1 68 34 H86 V52 A8 8 0 0 1 86 68 V86 H68 A8 8 0 0 0 52 86 H34 V68 A8 8 0 0 0 34 52 Z" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`),
        ],
    },
    {
        id: 'question',
        label: 'Question mark (brain)',
        category: 'themed',
        render: (op) => [
            tile(`<text x="40" y="88" font-size="72" font-family="sans-serif" font-weight="bold" fill="#fff" fill-opacity="${o3(op)}">?</text>`),
        ],
    },
    {
        id: 'lightbulb',
        label: 'Lightbulb (idea)',
        category: 'themed',
        render: (op) => [
            tile(`<path d="M60 26 A22 22 0 0 1 74 66 L46 66 A22 22 0 0 1 60 26 Z" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>` +
                `<rect x="50" y="68" width="20" height="7" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<rect x="52" y="78" width="16" height="6" rx="2" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'dice',
        label: 'Dice (game)',
        category: 'themed',
        render: (op) => [
            tile(`<rect x="30" y="30" width="60" height="60" rx="12" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>` +
                `<g fill="#fff" fill-opacity="${o3(op)}"><circle cx="45" cy="45" r="4"/><circle cx="75" cy="45" r="4"/>` +
                `<circle cx="60" cy="60" r="4"/><circle cx="45" cy="75" r="4"/><circle cx="75" cy="75" r="4"/></g>`),
        ],
    },
    {
        id: 'chess-pawns',
        label: 'Chess pawns (game)',
        category: 'themed',
        render: (op) => [
            tile(`<circle cx="60" cy="36" r="10" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<path d="M52 46 Q60 42 68 46 L64 66 L56 66 Z" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<path d="M48 70 L72 70 L77 92 L43 92 Z" fill="#fff" fill-opacity="${o3(op)}"/>`),
        ],
    },
    {
        id: 'smiley',
        label: 'Smiley (joy)',
        category: 'themed',
        render: (op) => [
            tile(`<circle cx="60" cy="60" r="34" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>` +
                `<circle cx="48" cy="52" r="4" fill="#fff" fill-opacity="${o3(op)}"/><circle cx="72" cy="52" r="4" fill="#fff" fill-opacity="${o3(op)}"/>` +
                `<path d="M45 68 Q60 82 75 68" fill="none" stroke="#fff" stroke-opacity="${o3(op)}" stroke-width="3"/>`),
        ],
    },
    {
        id: 'sun',
        label: 'Sun (happy)',
        category: 'themed',
        render: (op) => [tile(sunBody(op))],
    },
    // — painterly materials (SVG feTurbulence) —
    {
        id: 'watercolour',
        label: 'Watercolour',
        category: 'material',
        render: (op) => [turbulenceCover('wc', 'fractalNoise', '0.012', 3, op)],
    },
    {
        id: 'mist',
        label: 'Mist / clouds',
        category: 'material',
        render: (op) => [turbulenceCover('mist', 'fractalNoise', '0.006 0.009', 2, op)],
    },
    {
        id: 'paper-grain',
        label: 'Paper grain',
        category: 'material',
        render: (op) => [turbulenceCover('paper', 'turbulence', '0.9', 2, op * 0.7)],
    },
    {
        id: 'noise',
        label: 'Fine noise',
        category: 'material',
        render: (op) => [turbulenceCover('noise', 'fractalNoise', '0.7', 1, op * 0.6)],
    },
    {
        id: 'canvas',
        label: 'Canvas weave',
        category: 'material',
        render: (op) => [turbulenceCover('canvas', 'turbulence', '0.04 0.9', 2, op * 0.7)],
    },
];
export const MOTIF_REGISTRY = Object.fromEntries(MOTIF_LIST.map((m) => [m.id, m]));
/** Category display order + labels for grouped UI menus. */
export const MOTIF_CATEGORY_LABELS = {
    shapes: 'Shapes',
    dots: 'Dots & Halftone',
    lines: 'Lines',
    geometric: 'Geometric',
    occasion: 'Occasion & Seasonal',
    themed: 'Themed Icons',
    material: 'Materials',
};
export const MOTIF_CATEGORY_ORDER = [
    'shapes',
    'dots',
    'lines',
    'geometric',
    'occasion',
    'themed',
    'material',
];
/** Flat id list including 'none', for menus and random pools. */
export const MOTIFS = ['none', ...MOTIF_LIST.map((m) => m.id)];
/** Multiply the px values in a background-size (leaves 'cover'/'auto'/var() alone). */
function scaleSize(size, scale) {
    if (scale === 1)
        return size;
    return size.replace(/(\d+(?:\.\d+)?)px/g, (_m, n) => `${(parseFloat(n) * scale).toFixed(1)}px`);
}
/**
 * CSS layers for a motif id at the given opacity + tile scale ('none' → none).
 * `scale` > 1 makes tiled/dotted patterns sparser so they aren't too dense
 * across a full-screen background.
 */
export function renderMotifLayers(motif, opacity, scale = 1) {
    if (motif === 'none')
        return [];
    const def = MOTIF_REGISTRY[motif];
    if (!def)
        return [];
    return def.render(opacity).map((l) => ({ ...l, size: scaleSize(l.size, scale) }));
}
